/* Todaysunny 3.1 — deterministic utilities; no storefront state or network access. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.TSCommerce = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  const hosts = new Set([
    'amazon.com', 'www.amazon.com', 'amazon.ca', 'www.amazon.ca',
    'amazon.co.uk', 'www.amazon.co.uk', 'amazon.com.au', 'www.amazon.com.au',
    'amazon.de', 'www.amazon.de', 'amazon.fr', 'www.amazon.fr',
    'amazon.it', 'www.amazon.it', 'amazon.es', 'www.amazon.es',
    'amazon.co.jp', 'www.amazon.co.jp', 'amazon.com.mx', 'www.amazon.com.mx',
    'amazon.ae', 'www.amazon.ae', 'amazon.sa', 'www.amazon.sa', 'amzn.to', 'a.co'
  ]);
  const normalizeColor = (value) => String(value ?? '').normalize('NFKC').trim().toLowerCase();
  function validAmazonURL(value) {
    const raw = String(value ?? '').trim();
    if (!raw || /[\u0000-\u0020\\]/.test(raw)) return '';
    try {
      const url = new URL(raw);
      if (url.protocol !== 'https:' || url.username || url.password || url.port || !hosts.has(url.hostname)) return '';
      return url.href;
    } catch (_) { return ''; }
  }
  function matchesProductEvent(info, data) {
    if (!info || !data) return false;
    if (data.source) return data.source === info;
    const ids = [info.dataset?.originalSection, info.dataset?.section].filter(Boolean).map(String);
    if (!ids.includes(String(data.sectionId))) return false;
    const productId = data.productId || data.variant?.product_id;
    return !productId || !info.dataset?.productId || String(productId) === String(info.dataset.productId);
  }
  function visibleMediaIds(data, selectedColor) {
    const media = Array.isArray(data?.media) ? data.media : [];
    const all = media.map((item) => String(item.id));
    const selected = normalizeColor(selectedColor);
    const mode = data?.mode || 'auto';
    if (!selected || mode === 'off' || !media.length) return all;
    const rawAnchors = Array.isArray(data.anchors) ? data.anchors : [];
    const anchorSets = new Map();
    rawAnchors.forEach(({ id, color }) => {
      const key = String(id); const normalized = normalizeColor(color);
      if (!normalized || !all.includes(key)) return;
      if (!anchorSets.has(key)) anchorSets.set(key, new Set());
      anchorSets.get(key).add(normalized);
    });
    const anchors = new Map([...anchorSets].filter(([, colors]) => colors.size === 1).map(([id, colors]) => [id, [...colors][0]]));
    const known = new Set([...(data.colors || []), ...rawAnchors.map((a) => a.color)].map(normalizeColor));
    const assignments = new Map();
    const identifiable = new Set();
    let current = null;
    for (const item of media) {
      const id = String(item.id);
      if (anchors.has(id)) current = anchors.get(id);
      const alt = String(item.alt || '').trim();
      let explicit;
      if (/\[shared\]|#shared\b/i.test(alt)) explicit = null;
      else {
        const match = alt.match(/\[color:([^\]]+)\]|#color:([^\s#]+)/i);
        if (match) explicit = (match[1] || match[2]).split('|').map(normalizeColor).filter(Boolean);
        else if (known.has(normalizeColor(alt))) explicit = [normalizeColor(alt)];
      }
      let group = null;
      if (mode !== 'contiguous' && explicit !== undefined) group = explicit;
      else if (mode !== 'tags' && item.type === 'image') group = current ? [current] : null;
      if (group) group.forEach((color) => identifiable.add(color));
      assignments.set(id, group);
    }
    // Never present a blank/guessed gallery when a color cannot be mapped reliably.
    if (!identifiable.has(selected)) return all;
    const result = all.filter((id) => !assignments.get(id) || assignments.get(id).includes(selected));
    return result.length ? result : all;
  }
  function shippingState(rule, cart) {
    const threshold = Number(rule?.threshold);
    const total = Number(cart?.total);
    const countries = String(rule?.country || '').toUpperCase().split(',').map((s) => s.trim()).filter(Boolean);
    const visible = rule?.enabled === true && Number.isFinite(threshold) && threshold >= 0 && Number.isFinite(total) && total >= 0 &&
      cart?.requiresShipping === true && String(rule.currency).toUpperCase() === String(cart.currency).toUpperCase() &&
      countries.includes(String(cart.country).toUpperCase());
    if (!visible) return { visible: false };
    const remaining = Math.max(0, threshold - total);
    return { visible: true, remaining, progress: threshold ? Math.min(1, total / threshold) : 1, qualified: remaining === 0 };
  }
  return Object.freeze({ normalizeColor, validAmazonURL, matchesProductEvent, visibleMediaIds, shippingState });
});
